import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subscription, timer } from 'rxjs';

/** Ignore SignalR snapshots that are within this of the local wall-clock remaining. */
const DRIFT_TOLERANCE_MS = 1500;

@Component({
  selector: 'app-webinar-countdown',
  templateUrl: './webinar-countdown.component.html',
  styleUrls: ['./webinar-countdown.component.css'],
  standalone: false
})
export class WebinarCountdownComponent implements OnInit, OnChanges, OnDestroy {
  @Input() webinarName: string | undefined;
  /** Remaining seconds from the server at the moment this snapshot was taken. */
  @Input() secondsToStart: number | undefined;
  @Input() startAt: Date | string | undefined;
  @Input() serverNow: Date | string | undefined;
  @Output() remainingSecondsChange = new EventEmitter<number>();

  hours = '00';
  minutes = '00';
  seconds = '00';
  remainingSeconds = 0;
  progress = 1;
  isUrgent = false;
  isDue = false;
  showHours = false;
  secondPulse = false;

  private deadlineMs = 0;
  private initialMs = 0;
  private lastSecond = -1;
  private lastEmittedWhole = -1;
  private tickSub: Subscription | null = null;
  private pulseHandle: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.syncDeadline(true);
    this.tick();
    this.tickSub = timer(0, 200).subscribe(() => this.tick());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['secondsToStart'] && !changes['startAt'] && !changes['serverNow']) {
      return;
    }
    const firstBind = !!(
      changes['secondsToStart']?.firstChange ||
      changes['startAt']?.firstChange ||
      changes['serverNow']?.firstChange
    );
    this.syncDeadline(firstBind);
    this.tick();
  }

  ngOnDestroy(): void {
    this.tickSub?.unsubscribe();
    if (this.pulseHandle != null) {
      clearTimeout(this.pulseHandle);
    }
  }

  @HostListener('document:visibilitychange')
  onVisibility(): void {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      this.tick();
    }
  }

  @HostListener('window:pageshow')
  onPageShow(): void {
    this.tick();
  }

  @HostListener('window:focus')
  onFocus(): void {
    this.tick();
  }

  /**
   * Remaining time is wall-clock vs a deadline, not a decrementing counter.
   * Background tabs can throttle the interval, but Date.now() still jumps forward,
   * so returning to the tab (or another device loading later) stays aligned.
   */
  private syncDeadline(force = false): void {
    const remainingMs = this.resolveRemainingMs();
    const currentRemaining = Math.max(0, this.deadlineMs - Date.now());
    const drifted = Math.abs(remainingMs - currentRemaining) >= DRIFT_TOLERANCE_MS;
    if (!force && this.deadlineMs > 0 && !drifted) {
      return;
    }
    this.deadlineMs = Date.now() + remainingMs;
    if (!this.initialMs || remainingMs > this.initialMs + 500) {
      this.initialMs = Math.max(remainingMs, 1);
    }
  }

  private resolveRemainingMs(): number {
    const seconds = Number(this.secondsToStart);
    if (Number.isFinite(seconds)) {
      return Math.max(0, seconds * 1000);
    }
    const start = this.toMs(this.startAt);
    const server = this.toMs(this.serverNow);
    if (!Number.isNaN(start) && !Number.isNaN(server)) {
      return Math.max(0, start - server);
    }
    return 0;
  }

  private toMs(value: Date | string | number | undefined): number {
    if (value == null) {
      return NaN;
    }
    if (typeof value === 'number') {
      return value;
    }
    if (value instanceof Date) {
      return value.getTime();
    }
    return Date.parse(String(value));
  }

  private tick(): void {
    const remainingMs = Math.max(0, this.deadlineMs - Date.now());
    this.remainingSeconds = remainingMs / 1000;
    this.isDue = remainingMs <= 0;
    this.isUrgent = remainingMs > 0 && remainingMs <= 10000;
    this.progress = this.initialMs > 0 ? remainingMs / this.initialMs : 0;

    const total = Math.ceil(remainingMs / 1000);
    this.showHours = this.initialMs >= 3600000 || total >= 3600;
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    this.hours = this.pad(h);
    this.minutes = this.pad(m);
    this.seconds = this.pad(s);

    if (s !== this.lastSecond) {
      this.lastSecond = s;
      this.secondPulse = true;
      if (this.pulseHandle != null) {
        clearTimeout(this.pulseHandle);
      }
      this.pulseHandle = setTimeout(() => {
        this.secondPulse = false;
        this.pulseHandle = null;
      }, 180);
    }

    if (total !== this.lastEmittedWhole) {
      this.lastEmittedWhole = total;
      this.remainingSecondsChange.emit(this.remainingSeconds);
    }
  }

  private pad(value: number): string {
    return ('00' + value).slice(-2);
  }
}
